import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, updateEmail } from 'firebase/auth';
import { Row, Container, Card, Form, Col, Modal } from 'react-bootstrap';
import { Button } from '@material-ui/core';
import { css } from '@emotion/react';
import { TextField } from '@material-ui/core';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import PhoneNumberInput from '@/components/PhoneNumberInput';
import './styles.css';
import * as Sentry from '@sentry/react';
import { UPDATE_SHIPPER_CLIENT_INFO, UPDATE_USER_EMAIL } from '../mutations';
import { useMutation } from '@apollo/client';
import LocationPermissions from '../LocationPermissions';
import DataSharing from '../DataSharing';
import Communications from '../Communications';
import ProfileImageUpload from '../ProfileImageUpload';
import FleetSizeInput from '../FleetSizeInput';
import { displayPhone, storedPhone } from './contacts';
import { CUSTOM_CUSTOMER_COMMS_RANGE } from '@/constants/featureFlags';
import CommunicationsRange from '../CommunicationsRange';
import { shipperCommsFor } from './comms';
import { useClientUser } from '@/hooks';

function Profile(props) {
    const [supportEmail, setSupportEmail] = useState('');
    const [supportPhone, setSupportPhone] = useState('');
    const [primaryName, setPrimaryName] = useState('');
    const [secondaryName, setSecondaryName] = useState('');
    const [primaryEmail, setPrimaryEmail] = useState('');
    const [secondaryEmail, setSecondaryEmail] = useState('');
    const [primaryPhone, setPrimaryPhone] = useState('');
    const [secondaryPhone, setSecondaryPhone] = useState('');
    const [updateMessage, setUpdateMessage] = useState('');
    const [emailModal, setEmailModal] = useState(false);
    const [fleetSize, setFleetSize] = useState(null);
    const [validated, setValidated] = useState(false);
    const { device_settings } = props;
    const { circles, isImposter, isActingAsAccount, partner_carriers } = useClientUser();
    // updateEmail acts on the signed-in login, not this account's, so it's blocked while any admin acts as an account.
    const blockEmailChange = isActingAsAccount;

    const updatedComms = useMemo(() => shipperCommsFor({ partnerCarriers: partner_carriers }), [partner_carriers]);

    const [updateClientInfo] = useMutation(UPDATE_SHIPPER_CLIENT_INFO, {
        onCompleted: (data) => {},
        onError: (error) => {
            console.log(error);
        },
    });

    const [updateUserEmail] = useMutation(UPDATE_USER_EMAIL, {
        onCompleted: (data) => {},
        onError: (error) => {
            console.log(error);
        },
    });

    const EmailModal = (props) => {
        const { show, onHide } = props;

        return (
            <Modal show={show} onHide={onHide}>
                <Modal.Header>Email Change</Modal.Header>

                <Modal.Body>
                    Please be aware that changing your primary contact email will also change the login email for this
                    account.
                    <div className="text-danger">
                        {blockEmailChange ? 'Admins cannot change account emails at this time.' : ''}
                    </div>
                </Modal.Body>
            </Modal>
        );
    };

    const handleSubmit = async (event) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();

        if (form.checkValidity() === true) {
            let userUpdatePayload = {
                username: primaryName,
                secondary_username: secondaryName,
                email: blockEmailChange ? props.currentShipper.email : primaryEmail,
                secondary_email: secondaryEmail,
                business_phone: storedPhone(primaryPhone),
                secondary_phone: storedPhone(secondaryPhone),
                support_email: supportEmail,
                support_phone: storedPhone(supportPhone),
                ...(Number.isInteger(fleetSize) ? { fleet_size: fleetSize } : {}),
            };

            try {
                await updateClientInfo({
                    variables: {
                        client_id: props.currentShipperRef,
                        ...userUpdatePayload,
                    },
                });

                if (!blockEmailChange && primaryEmail !== props.currentShipper.email) {
                    // Update firebase email
                    const auth = getAuth();
                    const user = auth.currentUser;
                    await updateEmail(user, primaryEmail);

                    // Updates the email in the user table
                    await updateUserEmail({
                        variables: {
                            user_id: props.currentShipperRef,
                            email: primaryEmail,
                        },
                    });
                }

                setUpdateMessage('Profile updated!');
            } catch (error) {
                setUpdateMessage(error);
                console.log(error);
                Sentry.captureException(error);
            }
        } else {
            setUpdateMessage('Please fill out required fields');
        }
        setValidated(true);
    };

    const theme = createTheme({
        palette: {
            primary: {
                main: '#71b77a',
            },
        },
    });

    useEffect(() => {
        setSupportEmail(props.currentShipper.support_email || '');
        setPrimaryName(props.currentShipper.username);
        setSecondaryName(props.currentShipper.secondary_username || '');
        setPrimaryEmail(props.currentShipper.email);
        setSecondaryEmail(props.currentShipper.secondary_email || '');
        setFleetSize(props.currentShipper.fleet_size);
        setPrimaryPhone(displayPhone(props.currentShipper.business_phone));
        setSecondaryPhone(displayPhone(props.currentShipper.secondary_phone));
        setSupportPhone(displayPhone(props.currentShipper.support_phone));
    }, [props.currentShipper]);

    return (
        <>
            <Card className="tabCard">
                <Card.Body className="overflow-auto">
                    <Container fluid>
                        <ThemeProvider theme={theme}>
                            <Form id="accountDetails" noValidate validated={validated} onSubmit={handleSubmit}>
                                <Row
                                    css={css`
                                        display: flex;
                                    `}
                                >
                                    <Col>
                                        <Form.Group
                                            css={css`
                                                margin-top: 8px;
                                                margin-bottom: 16px;
                                            `}
                                        >
                                            <TextField
                                                fullWidth
                                                value={supportEmail || ''}
                                                variant="outlined"
                                                label={'Support Contact Email'}
                                                onChange={(e) => setSupportEmail(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group
                                            css={css`
                                                margin-top: 8px;
                                                margin-bottom: 16px;
                                            `}
                                        >
                                            <TextField
                                                fullWidth
                                                value={supportPhone || ''}
                                                variant="outlined"
                                                label={'Support Contact Phone'}
                                                onChange={(e) => setSupportPhone(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="d-flex">
                                    <Col>
                                        <Form.Group className="my-2 w-100">
                                            <TextField
                                                required
                                                error={!primaryName && validated}
                                                fullWidth
                                                className=""
                                                value={primaryName || ''}
                                                variant="outlined"
                                                label={'Name'}
                                                onChange={(e) => setPrimaryName(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="my-2 w-100">
                                            <TextField
                                                required
                                                fullWidth
                                                className=""
                                                error={!primaryEmail && validated}
                                                value={primaryEmail || ''}
                                                variant="outlined"
                                                label={'Email'}
                                                onChange={(e) => setPrimaryEmail(e.target.value)}
                                                onClick={(e) => setEmailModal(true)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="my-2 w-100">
                                            <TextField
                                                label="Phone"
                                                variant="outlined"
                                                className="w-100"
                                                required
                                                onChange={(e) => setPrimaryPhone(e.target.value)}
                                                name="phone"
                                                error={!primaryPhone && validated}
                                                value={primaryPhone}
                                                id="formatted-phone-input"
                                                InputProps={{
                                                    inputComponent: PhoneNumberInput,
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="d-flex">
                                    <Col>
                                        <Form.Group className="my-2 w-100">
                                            <TextField
                                                fullWidth
                                                className=""
                                                value={secondaryName || ''}
                                                variant="outlined"
                                                label={'Secondary Contact Name'}
                                                onChange={(e) => setSecondaryName(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="my-2 w-100">
                                            <TextField
                                                fullWidth
                                                className=""
                                                value={secondaryEmail || ''}
                                                variant="outlined"
                                                label={' Secondary Email'}
                                                onChange={(e) => setSecondaryEmail(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="my-2 w-100">
                                            <TextField
                                                label="Secondary Phone"
                                                variant="outlined"
                                                className="w-100"
                                                onChange={(e) => setSecondaryPhone(e.target.value)}
                                                name="Secondary Phone"
                                                value={secondaryPhone}
                                                id="formatted-phone-input"
                                                InputProps={{
                                                    inputComponent: PhoneNumberInput,
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <FleetSizeInput fleetSize={fleetSize} setFleetSize={setFleetSize} />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Button type="submit" form="accountDetails" className="my-2 ml-3 account-btn">
                                            Save
                                        </Button>
                                        {updateMessage && <div>{updateMessage}</div>}
                                    </Col>
                                </Row>
                            </Form>
                            {/* removed for the time being at Drew's request. May add back in later.
                        <h3>Blacklist</h3>
                        <div>
                          <p>
                            Here you may select carrier companies that do not wish to work with.
                            Orders that you place will not be visible to blacklisted carriers.
                          </p>
                        </div>
                        <Blacklist
                            blacklistType='shipper'
                            firebase={props.firebase}
                            currentUser={props.currentShipperRef}
                            update={addToBlacklist}
                            /> */}
                        </ThemeProvider>
                    </Container>
                </Card.Body>
                <EmailModal show={emailModal} onHide={() => setEmailModal(false)} />
            </Card>

            <Communications comms={updatedComms} />
            <LocationPermissions device_settings={device_settings} />
            <DataSharing dataSharing={props.currentShipper?.data_share} />
            <ProfileImageUpload client={props.currentShipper} />
            {isImposter && <ProfileImageUpload client={props.currentShipper} whiteLabel={true} />}
            {circles?.[CUSTOM_CUSTOMER_COMMS_RANGE] && <CommunicationsRange client={props.currentShipper} />}
        </>
    );
}

export default Profile;
